import { MediaPrompt } from './structs/media-spec';
import { isRecord, toStringValue } from './typeHelpers';

export const toReferenceImageUrl = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (!isRecord(value)) return '';

  return toStringValue(
    value.reference_image_url ??
      value.referenceImageUrl ??
      value.url ??
      value.reference_url ??
      value.referenceUrl ??
      value.media_id ??
      value.mediaId ??
      value.id
  ).trim();
};

/**
 * A reference the payload can BIND a row to: the bytes' URL, plus the media id
 * those bytes belong to when it is known.
 *
 * The id is not decoration. The backend's video request builder resolves
 * `reference_media_id` BEFORE `reference_image_url`
 * (contentgen-go-backend requestBuilder.go extractReferenceImageURL), so an id
 * left behind by a previous binding outranks the URL that replaced it: the
 * operator re-picks a take, the UI shows the new frame, and the generator is
 * handed the old one. A URL therefore never moves on its own — either the id
 * moves with it, or the id is removed so the URL is all that is left to obey.
 */
export interface ReferenceBinding {
  url: string;
  /** Media id of `url`, when the source of the binding knew one. */
  media_id?: string;
}

/** Bindings are accepted as bare URLs too, for callers that know no ids. */
export type ReferenceBindingLike = string | ReferenceBinding;

/** Normalises a binding-ish value; `undefined` when it names no bytes at all. */
export const toReferenceBinding = (
  value: ReferenceBindingLike | undefined
): ReferenceBinding | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') {
    const url = value.trim();
    return url ? { url } : undefined;
  }
  const url = toStringValue(value.url).trim();
  if (!url) return undefined;
  const mediaId = toStringValue(value.media_id).trim();
  return mediaId ? { url, media_id: mediaId } : { url };
};

/** The media id a row already carries, top level or in its metadata bag. */
const readRowReferenceMediaId = (
  entry: Record<string, unknown>,
  metadata: Record<string, unknown>
): string =>
  toStringValue(entry.reference_media_id ?? entry.referenceMediaId).trim() ||
  toStringValue(metadata.reference_media_id ?? metadata.referenceMediaId).trim();

/**
 * Writes the winning reference onto a row's metadata bag.
 *
 * `rebound` is the whole point: when the URL came from a binding source (the
 * operator's per-scene pick, or the positional list) the row's previous id is
 * about the previous bytes and must not survive — it is replaced by the new
 * one, or deleted when the source knew none. An untouched row keeps what it
 * had. The top-level keys are cleared on a rebind as well, because the clip API
 * flattens them into the same metadata bag and a stale one there would shadow
 * the answer just written.
 */
const bindRowReference = (
  entry: Record<string, unknown>,
  metadata: Record<string, unknown>,
  winner: { url: string; media_id?: string; rebound: boolean } | undefined
): void => {
  if (winner?.url) {
    metadata.reference_image_url = winner.url;
  } else {
    delete metadata.reference_image_url;
  }
  delete metadata.referenceImageUrl;

  const mediaId = winner?.rebound ? winner.media_id || '' : readRowReferenceMediaId(entry, metadata);

  if (mediaId) {
    metadata.reference_media_id = mediaId;
  } else {
    delete metadata.reference_media_id;
  }
  delete metadata.referenceMediaId;

  if (winner?.rebound) {
    delete entry.reference_media_id;
    delete entry.referenceMediaId;
  }
};

const readPromptReferenceImageFromOutputSpec = (prompt: MediaPrompt): string => {
  const promptRecord = prompt as Record<string, unknown>;
  const outputSpec = isRecord(promptRecord.outputSpec)
    ? promptRecord.outputSpec
    : isRecord(promptRecord.output_spec)
      ? promptRecord.output_spec
      : undefined;

  if (!outputSpec) return '';
  const referenceImages = Array.isArray(outputSpec.referenceImages)
    ? outputSpec.referenceImages
    : Array.isArray(outputSpec.reference_images)
      ? outputSpec.reference_images
      : [];

  if (referenceImages.length === 0) return '';
  const firstReference = referenceImages[0];
  return toReferenceImageUrl(
    firstReference && isRecord(firstReference) ? firstReference.inputImage ?? firstReference : firstReference
  );
};

export const stripLegacyReferenceImages = <T,>(value: T): T => {
  if (!isRecord(value)) return value;
  const next = { ...value };
  delete next.referenceImages;
  delete next.reference_images;
  return next as T;
};

export const stripPromptLegacyReferenceImages = (prompt: MediaPrompt): MediaPrompt => {
  const nextPrompt = { ...(prompt as Record<string, unknown>) };

  if (isRecord(nextPrompt.outputSpec)) {
    nextPrompt.outputSpec = stripLegacyReferenceImages(nextPrompt.outputSpec);
  }

  if (isRecord(nextPrompt.output_spec)) {
    nextPrompt.output_spec = stripLegacyReferenceImages(nextPrompt.output_spec);
  }

  return nextPrompt as MediaPrompt;
};

export const readTopLevelReferenceBindings = (metadata: unknown): ReferenceBinding[] => {
  if (!isRecord(metadata)) return [];

  const rows = [
    ...(Array.isArray(metadata.reference_assets) ? metadata.reference_assets : []),
    ...(Array.isArray(metadata.generated_reference_assets) ? metadata.generated_reference_assets : []),
  ];

  return rows
    .map((item) => {
      const url = toReferenceImageUrl(item);
      if (!url) return undefined;
      const mediaId = isRecord(item)
        ? toStringValue(item.media_id ?? item.mediaId).trim()
        : '';
      return mediaId ? { url, media_id: mediaId } : { url };
    })
    .filter((binding): binding is ReferenceBinding => Boolean(binding));
};

/** @deprecated Reads as bindings now; the name survives for callers elsewhere. */
export const readTopLevelReferenceImageUrls = readTopLevelReferenceBindings;

/**
 * The scene a prompt row (or a reference asset) belongs to.
 *
 * Binding references to scenes MUST go through this. A run's asset lists are
 * built in checkpoint order, so an upload checkpoint at the start of a pipeline
 * puts a scene-less still at index 0 — and anything that then zips those URLs
 * onto the scene rows by ARRAY POSITION hands scene N the still of scene N-1.
 */
export const readSceneId = (value: unknown): string => {
  if (!isRecord(value)) return '';
  const direct = toStringValue(value.scene_id ?? value.sceneId).trim();
  if (direct) return direct;
  const metadata = isRecord(value.metadata) ? value.metadata : undefined;
  return metadata ? toStringValue(metadata.scene_id ?? metadata.sceneId).trim() : '';
};

/**
 * scene_id → reference URL, collected from every scene-tagged source a clip
 * payload can carry: the explicit scene→reference mapping the assembly step
 * writes, and the run's reference assets (which keep the backend's scene tag).
 * Untagged entries — an upload, a hydrated seed — are simply absent, which is
 * exactly what stops them being handed to a scene.
 */
export const readSceneReferenceBindings = (metadata: unknown): Map<string, ReferenceBinding> => {
  const bySceneId = new Map<string, ReferenceBinding>();
  if (!isRecord(metadata)) return bySceneId;

  const lists = [
    metadata.scene_reference_mapping,
    metadata.scene_references,
    metadata.scene_reference_bindings,
    metadata.reference_assets,
    metadata.generated_reference_assets,
  ];

  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((row) => {
      if (!isRecord(row)) return;
      const sceneId = readSceneId(row);
      if (!sceneId || bySceneId.has(sceneId)) return;
      const url = toStringValue(
        row.reference_url ?? row.reference_image_url ?? row.url ?? row.file_url
      ).trim();
      if (!url) return;
      // The mapping row names the media it resolved; carrying it is what lets a
      // rebind replace the row's id instead of leaving the old one to win.
      const mediaId = toStringValue(
        row.reference_media_id ?? row.media_id ?? row.mediaId
      ).trim();
      bySceneId.set(sceneId, mediaId ? { url, media_id: mediaId } : { url });
    });
  });

  return bySceneId;
};

/** @deprecated Reads as bindings now; the name survives for callers elsewhere. */
export const readSceneReferenceImageUrls = readSceneReferenceBindings;

/** The reference the row already carries — metadata copy first, then top level. */
const readRowReferenceImageUrl = (entry: Record<string, unknown>): string => {
  const metadata = isRecord(entry.metadata) ? entry.metadata : {};
  return (
    toReferenceImageUrl(metadata.reference_image_url ?? metadata.referenceImageUrl) ||
    toReferenceImageUrl(entry.reference_image_url ?? entry.referenceImageUrl)
  );
};

export const normalizePromptReferenceImageUrls = (
  prompts: MediaPrompt[] | undefined,
  fallbackReferenceImageUrls: ReferenceBindingLike[],
  sceneReferenceImageUrls?: Map<string, ReferenceBindingLike>
): MediaPrompt[] | undefined => {
  if (!prompts?.length) return prompts;

  return prompts.map((prompt, index) => {
    const nextPrompt = stripPromptLegacyReferenceImages(prompt);
    const promptRecord = nextPrompt as Record<string, unknown>;
    const metadata = isRecord(promptRecord.metadata) ? { ...promptRecord.metadata } : {};
    const sceneId = readSceneId(promptRecord);
    const explicitReferenceImageUrl = readRowReferenceImageUrl(promptRecord);
    // A row that knows its scene is bound by scene_id or not at all: falling
    // back to the row's INDEX in a checkpoint-ordered list is the off-by-one.
    const sceneFallback = sceneId
      ? toReferenceBinding(sceneReferenceImageUrls?.get(sceneId))
      : toReferenceBinding(fallbackReferenceImageUrls[index] ?? fallbackReferenceImageUrls[0]);
    const rowUrl = explicitReferenceImageUrl || readPromptReferenceImageFromOutputSpec(prompt);

    // Only the fallback is a REBIND — the row's own reference is the binding it
    // already had, id included.
    const winner = rowUrl
      ? { url: rowUrl, rebound: false }
      : sceneFallback
        ? { url: sceneFallback.url, media_id: sceneFallback.media_id, rebound: true }
        : undefined;

    bindRowReference(promptRecord, metadata, winner);

    promptRecord.metadata = metadata;
    return promptRecord as MediaPrompt;
  });
};

/**
 * Stamps `metadata.reference_image_url` — and the id that goes with it — on
 * scene rows.
 *
 * `sceneReferenceImageUrls` is the operator's per-scene binding and wins where
 * it has an entry. A row that carries a scene_id is never bound positionally —
 * it keeps whatever reference it already had rather than borrowing its
 * neighbour's. Positional matching survives only for scene-less payloads, where
 * there is nothing else to go on.
 */
export const applyReferenceImageMetadata = (
  value: unknown,
  referenceImageUrls: ReferenceBindingLike[],
  sceneReferenceImageUrls?: Map<string, ReferenceBindingLike>
): unknown => {
  if (!Array.isArray(value)) return value;

  return value.map((entry, index) => {
    if (!isRecord(entry)) return entry;
    const nextEntry: Record<string, unknown> = { ...entry };

    if (isRecord(entry.outputSpec)) {
      nextEntry.outputSpec = stripLegacyReferenceImages(entry.outputSpec);
    }
    if (isRecord(entry.output_spec)) {
      nextEntry.output_spec = stripLegacyReferenceImages(entry.output_spec);
    }

    const metadata = isRecord(entry.metadata) ? { ...entry.metadata } : {};
    const sceneId = readSceneId(entry);
    const sceneBinding = sceneId
      ? toReferenceBinding(sceneReferenceImageUrls?.get(sceneId))
      : undefined;
    const positional = sceneId
      ? undefined
      : toReferenceBinding(referenceImageUrls[index] ?? referenceImageUrls[0]);
    const rowUrl = readRowReferenceImageUrl(entry);

    // The operator's per-scene pick wins outright — this is the re-pick path,
    // and it is a REBIND: the row's old media id goes with the old URL.
    const winner = sceneBinding
      ? { url: sceneBinding.url, media_id: sceneBinding.media_id, rebound: true }
      : rowUrl
        ? { url: rowUrl, rebound: false }
        : positional
          ? { url: positional.url, media_id: positional.media_id, rebound: true }
          : undefined;

    bindRowReference(nextEntry, metadata, winner);

    nextEntry.metadata = metadata;
    return nextEntry;
  });
};
